// src/store/slices/adminManagementSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../services/api/axios';

// API Routes
const API_ROUTES = {
  GET_ALL_ADMINS: '/admin/list',
  CREATE_ADMIN: '/admin/create',
  GET_ADMIN: '/admin',
  UPDATE_ADMIN: '/admin',
  UPDATE_ADMIN_PASSWORD: '/admin',
  DELETE_ADMIN: '/admin',
  GENERATE_INIT_KEY: '/admin/init-key/generate',
  GET_INIT_KEY_STATUS: '/admin/init-key/status',
  REVOKE_INIT_KEY: '/admin/init-key/revoke',
  
  // NEW: Self-service routes
  CHANGE_PASSWORD: '/admin/change-password',
  UPDATE_PROFILE: '/admin/profile',
  GET_PROFILE: '/admin/profile'
};

// Thunk Actions
export const getAllAdmins = createAsyncThunk(
  'adminManagement/getAllAdmins',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(API_ROUTES.GET_ALL_ADMINS);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch admins' });
    }
  }
);

export const createAdmin = createAsyncThunk(
  'adminManagement/createAdmin',
  async ({ adminData, initKey }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        API_ROUTES.CREATE_ADMIN, 
        adminData,
        {
          headers: {
            'x-init-key': initKey
          }
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to create admin' });
    }
  }
);

export const getAdminById = createAsyncThunk(
  'adminManagement/getAdminById',
  async (adminId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${API_ROUTES.GET_ADMIN}/${adminId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch admin details' });
    }
  }
);

export const updateAdmin = createAsyncThunk(
  'adminManagement/updateAdmin',
  async ({ adminId, data }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`${API_ROUTES.UPDATE_ADMIN}/${adminId}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to update admin' });
    }
  }
);

// For Super Admin to update OTHER admins' passwords (no current password required)
export const updateAdminPassword = createAsyncThunk(
  'adminManagement/updateAdminPassword',
  async ({ adminId, password }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${API_ROUTES.UPDATE_ADMIN_PASSWORD}/${adminId}/password`, 
        { password }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to update password' });
    }
  }
);

// NEW: For admins with MANAGE_ADMINS permission to change their OWN password (requires current password)
export const changeOwnPassword = createAsyncThunk(
  'adminManagement/changeOwnPassword',
  async ({ currentPassword, newPassword }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        API_ROUTES.CHANGE_PASSWORD,
        { 
          currentPassword, 
          newPassword 
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to change password' });
    }
  }
);

// NEW: For admins with MANAGE_ADMINS permission to update their OWN profile
export const updateOwnProfile = createAsyncThunk(
  'adminManagement/updateOwnProfile',
  async ({ name, email, phone, currentPassword }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        API_ROUTES.UPDATE_PROFILE,
        { 
          name, 
          email, 
          phone, 
          currentPassword 
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to update profile' });
    }
  }
);

// NEW: Get own profile (requires MANAGE_ADMINS permission)
export const getOwnProfile = createAsyncThunk(
  'adminManagement/getOwnProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(API_ROUTES.GET_PROFILE);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch profile' });
    }
  }
);

export const deleteAdmin = createAsyncThunk(
  'adminManagement/deleteAdmin',
  async (adminId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(`${API_ROUTES.DELETE_ADMIN}/${adminId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to delete admin' });
    }
  }
);

export const generateInitKey = createAsyncThunk(
  'adminManagement/generateInitKey',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/admin/init-key/generate');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to generate initialization key' });
    }
  }
);

export const getInitKeyStatus = createAsyncThunk(
  'adminManagement/getInitKeyStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/admin/init-key/status');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch init key status' });
    }
  }
);

export const revokeInitKey = createAsyncThunk(
  'adminManagement/revokeInitKey',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete('/admin/init-key/revoke');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to revoke initialization key' });
    }
  }
);

const initialState = {
  admins: [],
  currentAdmin: null,
  loading: false,
  error: null,
  success: false,
  
  // Init key fields
  initKeyStatus: null,
  generatedInitKey: null,
  initKeyLoading: false,
  initKeyError: null,
  
  // Profile update fields
  profileLoading: false,
  profileError: null,
  profileSuccess: false
};

const adminManagementSlice = createSlice({
  name: 'adminManagement',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.profileError = null;
      state.initKeyError = null;
    },
    clearSuccess: (state) => {
      state.success = false;
      state.profileSuccess = false;
    },
    clearGeneratedInitKey: (state) => {
      state.generatedInitKey = null;
    },
    resetAdminState: () => initialState
  },
  extraReducers: (builder) => {
    builder
      // Get All Admins
      .addCase(getAllAdmins.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllAdmins.fulfilled, (state, action) => {
        state.loading = false;
        state.admins = action.payload.data.admins;
      })
      .addCase(getAllAdmins.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch admins';
      })

      // Create Admin
      .addCase(createAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createAdmin.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.admins.push(action.payload.data);
      })
      .addCase(createAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to create admin';
      })

      // Get Admin By Id
      .addCase(getAdminById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAdminById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentAdmin = action.payload.data.admin;
      })
      .addCase(getAdminById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch admin details';
      })

      // Update Admin (by Super Admin)
      .addCase(updateAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateAdmin.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        const updatedAdmin = action.payload.data.admin;
        state.admins = state.admins.map(admin => 
          admin.adminId === updatedAdmin.adminId ? updatedAdmin : admin
        );
        if (state.currentAdmin?.adminId === updatedAdmin.adminId) {
          state.currentAdmin = updatedAdmin;
        }
      })
      .addCase(updateAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to update admin';
      })

      // Update Admin Password (by Super Admin)
      .addCase(updateAdminPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateAdminPassword.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(updateAdminPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to update password';
      })

      // Change Own Password
      .addCase(changeOwnPassword.pending, (state) => {
        state.profileLoading = true;
        state.profileError = null;
        state.profileSuccess = false;
      })
      .addCase(changeOwnPassword.fulfilled, (state) => {
        state.profileLoading = false;
        state.profileSuccess = true;
      })
      .addCase(changeOwnPassword.rejected, (state, action) => {
        state.profileLoading = false;
        state.profileError = action.payload?.message || action.error?.message || 'Failed to change password';
      })

      // Update Own Profile
      .addCase(updateOwnProfile.pending, (state) => {
        state.profileLoading = true;
        state.profileError = null;
        state.profileSuccess = false;
      })
      .addCase(updateOwnProfile.fulfilled, (state, action) => {
        state.profileLoading = false;
        state.profileSuccess = true;
        // Update the admin in auth state should be handled by auth slice
      })
      .addCase(updateOwnProfile.rejected, (state, action) => {
        state.profileLoading = false;
        state.profileError = action.payload?.message || action.error?.message || 'Failed to update profile';
      })

      // Get Own Profile
      .addCase(getOwnProfile.pending, (state) => {
        state.profileLoading = true;
        state.profileError = null;
      })
      .addCase(getOwnProfile.fulfilled, (state, action) => {
        state.profileLoading = false;
        // Profile data should be stored in auth slice
      })
      .addCase(getOwnProfile.rejected, (state, action) => {
        state.profileLoading = false;
        state.profileError = action.payload?.message || 'Failed to fetch profile';
      })

      // Delete Admin
      .addCase(deleteAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteAdmin.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.admins = state.admins.filter(admin => admin.adminId !== action.meta.arg);
        if (state.currentAdmin?.adminId === action.meta.arg) {
          state.currentAdmin = null;
        }
      })
      .addCase(deleteAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to delete admin';
      })

      // Generate Init Key
      .addCase(generateInitKey.pending, (state) => {
        state.initKeyLoading = true;
        state.initKeyError = null;
        state.generatedInitKey = null;
      })
      .addCase(generateInitKey.fulfilled, (state, action) => {
        state.initKeyLoading = false;
        state.generatedInitKey = action.payload.data;
      })
      .addCase(generateInitKey.rejected, (state, action) => {
        state.initKeyLoading = false;
        state.initKeyError = action.payload?.message || 'Failed to generate init key';
      })
      
      // Get Init Key Status
      .addCase(getInitKeyStatus.pending, (state) => {
        state.initKeyLoading = true;
        state.initKeyError = null;
      })
      .addCase(getInitKeyStatus.fulfilled, (state, action) => {
        state.initKeyLoading = false;
        state.initKeyStatus = action.payload.data;
      })
      .addCase(getInitKeyStatus.rejected, (state, action) => {
        state.initKeyLoading = false;
        state.initKeyError = action.payload?.message || 'Failed to fetch init key status';
      })
      
      // Revoke Init Key
      .addCase(revokeInitKey.pending, (state) => {
        state.initKeyLoading = true;
        state.initKeyError = null;
      })
      .addCase(revokeInitKey.fulfilled, (state) => {
        state.initKeyLoading = false;
        state.initKeyStatus = null;
        state.generatedInitKey = null;
      })
      .addCase(revokeInitKey.rejected, (state, action) => {
        state.initKeyLoading = false;
        state.initKeyError = action.payload?.message || 'Failed to revoke init key';
      });
  }
});

export const { clearError, clearSuccess, clearGeneratedInitKey, resetAdminState } = adminManagementSlice.actions;
export default adminManagementSlice.reducer;